import { useQueryParam, useScrollToTopOnMount, type SetQueryParam } from "hooks"
import { debounce } from "lodash-es"
import { Card, Icon } from "materia"
import { forwardRef, useMemo, type InputHTMLAttributes } from "react"
import { useTranslation } from "react-i18next"
import { ExploreFilteredProducts } from "../../components/FilteredProducts"
import { HiddenHeader } from "../../components/HiddenHeader"
import type { ProductTypeIdentifier } from "../../graphql"
import { usePageTitle } from "../../tools/usePageTitle"
import { ExploreFiltersConnected } from "./ExploreFilters"
import s from "./ExploreView.module.scss"

// [TODO] https://jira.attensi.com/browse/WEB-21038
export const REAL_TALK_PRODUCT_TYPES: ProductTypeIdentifier[] = [
  "real_talk",
  "real_talk_maker",
  "skills_real_talk",
]

// FAME-223: the "Course" filter groups scorm and xapi behind a single button,
// the same way real_talk groups its three types above. "scorm" is the
// representative type stored in the query param.
export const COURSE_PRODUCT_TYPES: ProductTypeIdentifier[] = ["scorm", "xapi"]

export const ExploreView = () => {
  useScrollToTopOnMount()

  const { t } = useTranslation()
  usePageTitle(t("pageTitle.EXPLORE"))

  const [search, setSearch] = useQueryParam("search")
  const [selectedProductType, setSelectedProductType] =
    useQueryParam<ProductTypeIdentifier>("product_type")

  // [TODO] https://jira.attensi.com/browse/WEB-21038
  // Only one product_type can be selected at a time, what is reflected in query params.
  // In case of real_talk, we need to show all three real_talk types.
  // Query param shows only one type (real_talk) but we make a GraphQL query to get all 3.
  const getFilteredProductTypes = (): ProductTypeIdentifier[] | undefined => {
    if (selectedProductType === "real_talk") return REAL_TALK_PRODUCT_TYPES
    if (selectedProductType === "scorm") return COURSE_PRODUCT_TYPES
    if (selectedProductType) return [selectedProductType]
    return undefined
  }

  return (
    <div className={s.exploreView}>
      <HiddenHeader title={t(`navigation.EXPLORE`)} tag="h1" />

      <Card>
        <Card.Content>
          <search>
            <Search search={search} setSearch={setSearch} />

            <ExploreFiltersConnected
              selectedProductType={selectedProductType}
              setSelectedProductType={setSelectedProductType}
            />
          </search>
        </Card.Content>
      </Card>

      <HiddenHeader title={t(`product.PRODUCTS`)} tag="h2" />

      <ExploreFilteredProducts
        input={{
          search,
          productTypes: getFilteredProductTypes(),
        }}
      />
    </div>
  )
}

const Search = ({
  search,
  setSearch,
}: {
  search: string | undefined
  setSearch: SetQueryParam<string>
}) => {
  const { t } = useTranslation()

  const debouncedOnSearch = useMemo(() => debounce(setSearch, 500), [setSearch])

  const onKeyDown = (key: string, value: string) => {
    if (key === "Enter") setSearch(value)
  }

  return (
    <SearchInput
      defaultValue={search}
      placeholder={t("searchbar.PLACEHOLDER")}
      onKeyDown={(e) => onKeyDown(e.key, e.currentTarget.value)}
      onChange={(e) => debouncedOnSearch(e.currentTarget.value)}
    />
  )
}

const SearchInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>((props, ref) => {
  const { t } = useTranslation()

  return (
    <div className={s.search}>
      <Icon icon="Search" size="large" className={s.search__icon} />
      <label className={s.search__label}>
        {t(`pages.SEARCH_LABEL`)}
        <input ref={ref} className={s.search__input} {...props} />
      </label>
    </div>
  )
})
